import { I18n } from '@lingui/core'
import { withI18n } from '@lingui/react'
import {
  type NowMerchantExtended,
  type NowSellerDataForCart,
} from '@redteclab/api/clients/bully'
import { shape, string } from 'prop-types'
import {
  type Dispatch,
  type FunctionComponent,
  type SetStateAction,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { compose } from 'redux'

import 'views/components/organisms/NowShippingModal/NowShippingModal.scss'
import {
  PAGE_TYPE_CATEGORY,
  PAGE_TYPE_SEARCH_RESULTS,
  TENANT_AT,
} from 'shared/consts'
import {
  NOW_CATEGORIES_SHIPPING_MODAL_SUBMIT,
  NOW_SERP_SHIPPING_MODAL_SUBMIT,
  type TrackingEventName,
} from 'shared/experiments/consts'
import {
  CART_GOT_ELIGIBLE_BY_DELETE,
  CART_GOT_ELIGIBLE_BY_QUANTITY_CHANGE,
} from 'shared/experiments/consts/events'
import trackOptimizelyEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import {
  getMergedCartItems,
  getNowMappedCartItems,
  updateCartWithNowData,
} from 'shared/store/ducks/cart'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import { type IState, type NowDataState } from 'types/redux'
import NowZipForm, {
  type INowDeliveryBoxData,
  type INowSlotExtended,
} from 'views/components/organisms/NowShippingModal/NowShippingModalContent/NowZipForm'
import { trackEligibleProduct } from 'views/components/organisms/ProductNowShippingModal'
import {
  MINIMUM_VALUE_NOT_REACHED,
  PRODUCTS_NOT_AVAILABLE,
} from 'views/consts/notEligibleReasons'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import buildClientAPI from 'views/providers/clientAPI'
import { publicRuntimeConfigType } from 'views/utils/propTypes'
import { type i18nType } from 'views/utils/propTypes/i18nType'

export const validateZipCode = (
  zipCode: string,
  i18n: I18n,
  setErrorMessage: Dispatch<SetStateAction<string>>,
  tenant: string
) => {
  const zipCodeLength = tenant === TENANT_AT ? 4 : 5
  setErrorMessage('')
  if (zipCode.length < zipCodeLength) {
    setErrorMessage(
      i18n._('form.error.input.minLength', { minLength: zipCodeLength })
    )
    return false
  }

  if (zipCode.length > zipCodeLength) {
    setErrorMessage(
      i18n._('form.error.input.maxLength', { maxLength: zipCodeLength })
    )
    return false
  }

  if (!Number(zipCode)) {
    setErrorMessage(i18n._('address.error.zip.invalid'))
    return false
  }
  return true
}

const NowShippingModalContent: FunctionComponent<
  NowShippingModalContentProps
> = ({ productUpid, i18n }) => {
  const [notEligibleReasonCode, setNotEligibleReasonCode] = useState<
    number | undefined
  >(0)
  const nowDataState: NowDataState = useSelector(
    (state: IState) => state.nowData
  )
  const [zipCode, setZipCode] = useState(nowDataState?.zipCode)
  const [errorMessage, setErrorMessage] = useState('')
  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
      pageProperties: { pageType },
    },
  } = useShopAndRuntimeConfig()

  const [nowEligible, setNowEligible] = useState(false)
  const [nowDeliveryBoxData, setNowDeliveryBoxData] =
    useState<INowDeliveryBoxData | null>(null)

  const [refreshNowData, setRefreshNowData] = useState(true)

  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)

  const makeState = (data: NowDataState) => {
    setNowEligible(data.nowEligible)
    setNotEligibleReasonCode(data.notEligibleReason)

    const slot = data?.slots?.length ? data.slots[0] : []

    setNowDeliveryBoxData({
      slot: { ...slot } as INowSlotExtended,
      merchant: { ...data.merchant } as NowMerchantExtended,
      centsDifferenceMinimumOrderValue: data.centsDifferenceMinimumOrderValue,
    })
  }

  const EVENT_MAP = {
    [PAGE_TYPE_SEARCH_RESULTS]: NOW_SERP_SHIPPING_MODAL_SUBMIT,
    [PAGE_TYPE_CATEGORY]: NOW_CATEGORIES_SHIPPING_MODAL_SUBMIT,
  }
  const event: TrackingEventName = EVENT_MAP[pageType as keyof typeof EVENT_MAP]

  const handleSubmit = () => {
    if (validateZipCode(zipCode, i18n, setErrorMessage, tenant)) {
      dispatch(
        updateNowDataSuccessAction({
          ...nowDataState,
          isNowCallNeeded: true,
          zipCode,
        })
      )
      trackOptimizelyEvent(event)
    }
  }

  const fetchNowData = async (
    zipCodeToUse: string,
    forceNowApiCall: boolean
  ) => {
    try {
      setIsLoading(true)

      if (nowDataState) {
        makeState(nowDataState)
      }

      if (!nowDataState?.isNowCallNeeded && !forceNowApiCall) {
        setIsLoading(false)
        return
      }

      if (zipCodeToUse) {
        const { nowService } = buildClientAPI()
        let response: NowSellerDataForCart

        if (!productUpid) {
          const cartResponse = getMergedCartItems({
            cartData: null,
            deleteOfferId: nowDataState?.deleteOfferId,
          })

          const products = getNowMappedCartItems(
            cartResponse,
            undefined,
            undefined
          )

          response = await nowService.fetchBasicData({
            zipCode: zipCodeToUse,
            products,
          })
          setNotEligibleReasonCode(response.notEligibleReason)
          setNowEligible(!!response.nowEligible)
        } else {
          response = await nowService.fetchBasicData({
            zipCode: zipCodeToUse,
            products: [
              {
                upid: productUpid,
                quantity: 1,
                priceCentsGross: 1,
                nowProduct: 0,
              },
            ],
          })

          let notEligibleReason: number | undefined = response.notEligibleReason

          if (notEligibleReason === MINIMUM_VALUE_NOT_REACHED) {
            notEligibleReason = undefined
          }

          const nowEligibleProduct: boolean =
            response.nowEligible ||
            response.notEligibleReason === MINIMUM_VALUE_NOT_REACHED
          setNowEligible(nowEligibleProduct)
          trackOptimizelyEvent(trackEligibleProduct(nowEligibleProduct))
          setNotEligibleReasonCode(notEligibleReason)
        }

        if (response?.nowEligible && !nowDataState?.nowEligible) {
          const eventType = nowDataState.eventType

          switch (eventType) {
            case 'delete':
              trackOptimizelyEvent(CART_GOT_ELIGIBLE_BY_DELETE)
              break
            case 'update':
              trackOptimizelyEvent(CART_GOT_ELIGIBLE_BY_QUANTITY_CHANGE)
              break
          }
        }

        const slot = response?.slots?.length ? response.slots[0] : []

        setNowDeliveryBoxData({
          slot: { ...slot } as INowSlotExtended,
          merchant: { ...response.merchant } as NowMerchantExtended,
          centsDifferenceMinimumOrderValue:
            response.centsDifferenceMinimumOrderValue,
        })

        setRefreshNowData(false)

        dispatch(
          updateNowDataSuccessAction({
            ...response,
            zipCode: zipCodeToUse,
            isNowCallNeeded: false,
            deleteOfferId: undefined,
            eventType: undefined,
          })
        )
        dispatch(updateCartWithNowData(response))
      } else {
        setNotEligibleReasonCode(undefined)
        dispatch(
          updateNowDataSuccessAction({
            zipCode: zipCodeToUse,
            isNowCallNeeded: false,
            deleteOfferId: undefined,
            eventType: undefined,
          })
        )

        dispatch(updateCartWithNowData(undefined))
      }

      setIsLoading(false)
    } catch (error) {
      setNotEligibleReasonCode(PRODUCTS_NOT_AVAILABLE)
      setNowEligible(false)

      dispatch(
        updateNowDataSuccessAction({
          zipCode: zipCodeToUse,
          nowEligible: false,
          notEligibleReason: PRODUCTS_NOT_AVAILABLE,
        })
      )

      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (refreshNowData) {
      void fetchNowData(nowDataState?.zipCode, !!productUpid)
    }
    setRefreshNowData(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productUpid, nowDataState?.products, nowDataState?.isNowCallNeeded])

  useEffect(() => {
    if (zipCode != nowDataState?.zipCode) {
      setZipCode(nowDataState?.zipCode)
      void fetchNowData(nowDataState?.zipCode, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowDataState?.zipCode])

  return (
    <NowZipForm
      nowEligible={nowEligible}
      zipCode={zipCode}
      setZipCode={setZipCode}
      setErrorMessage={setErrorMessage}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      nowDeliveryBoxData={nowDeliveryBoxData}
      notEligibleReasonCode={notEligibleReasonCode}
      errorMessage={errorMessage}
    />
  )
}

const props = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  i18n: I18n,
  productUpid: string,
}

type NowShippingModalContentProps = InferPropsWithDefaults<
  typeof props,
  typeof i18nType
>

export default compose(
  withShopAndRuntimeConfig,
  withI18n()
)(NowShippingModalContent)
